// Los cinco selectores de la clasificación (especialidad, partida, recinto,
// elemento y tipo de falla), extraídos de ClassifyFindingModal para que el
// formulario de convertir una consulta (ADR-0163) use exactamente los mismos.
// Etiquetas humanas, nunca códigos. El recinto y el elemento son listas
// PLANAS (no se filtran entre sí); la partida cuelga de la especialidad.
//
// Controlado: el padre guarda los valores. `sugeridos` pinta el tag
// «Sugerido por la IA» FUERA del <label> (el nombre accesible del campo no
// cambia) y el padre lo apaga cuando la persona edita ese campo.

import { useMemo } from "react";
import { SUGERIDO_POR_LA_IA } from "@bitacorapro/api-types";
import type { TaxonomyCatalog, TaxonomyRecinto } from "@bitacorapro/core-engine";
import styles from "./ClassifyFindingModal.module.css";

export type ClasificacionValue = {
  especialidad: string;
  partida: string;
  recinto: string;
  elemento: string;
  tipoFalla: string;
};
export type ClasificacionCampo = keyof ClasificacionValue;

export function ClasificacionFields({
  catalog,
  value,
  onChange,
  idPrefix,
  especialidades,
  recintos,
  partidaLabel = "Partida",
  sugeridos,
  disabled = false,
}: {
  catalog: TaxonomyCatalog;
  value: ClasificacionValue;
  /** Un campo cambió. Cambiar la especialidad limpia la partida. */
  onChange: (next: ClasificacionValue, campo: ClasificacionCampo) => void;
  idPrefix: string;
  /** Las especialidades que se ofrecen (default: todas). */
  especialidades?: TaxonomyCatalog["especialidades"];
  /** Los recintos que se ofrecen (default: todos). */
  recintos?: readonly TaxonomyRecinto[];
  partidaLabel?: string;
  sugeridos?: Partial<Record<ClasificacionCampo, boolean>>;
  disabled?: boolean;
}) {
  const listaEsp = especialidades ?? catalog.especialidades;
  const listaRecintos = recintos ?? catalog.recintos;
  const partidas = useMemo(
    () => catalog.especialidades.find((e) => e.codigo === value.especialidad)?.partidas ?? [],
    [catalog, value.especialidad]
  );
  // Una partida heredada que no es de la especialidad elegida se pinta vacía
  // (el <select> controlado no la tiene entre sus <option>).
  const partidaValid = partidas.some((p) => p.codigo === value.partida);
  const set = (campo: ClasificacionCampo, v: string) =>
    onChange(
      campo === "especialidad"
        ? { ...value, especialidad: v, partida: "" }
        : { ...value, [campo]: v },
      campo
    );
  const tag = (campo: ClasificacionCampo) =>
    sugeridos?.[campo] ? <span className="st-pill st-pill--info">{SUGERIDO_POR_LA_IA}</span> : null;

  return (
    <>
      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor={`${idPrefix}-especialidad`}>
          Especialidad
        </label>
        {tag("especialidad")}
        <select
          id={`${idPrefix}-especialidad`}
          className="input"
          value={value.especialidad}
          disabled={disabled}
          onChange={(e) => set("especialidad", e.target.value)}
        >
          <option value="">Selecciona una especialidad…</option>
          {listaEsp.map((e) => (
            <option key={e.codigo} value={e.codigo}>
              {e.nombre}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor={`${idPrefix}-partida`}>
          {partidaLabel}
        </label>
        {tag("partida")}
        <select
          id={`${idPrefix}-partida`}
          className="input"
          value={partidaValid ? value.partida : ""}
          onChange={(e) => set("partida", e.target.value)}
          disabled={disabled || !value.especialidad}
        >
          <option value="">
            {value.especialidad
              ? `Selecciona ${partidaLabel === "Partida" ? "una partida" : "el tipo de reparación"}…`
              : "Elige primero la especialidad"}
          </option>
          {partidas.map((p) => (
            <option key={p.codigo} value={p.codigo}>
              {p.nombre}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor={`${idPrefix}-recinto`}>
          Recinto
        </label>
        {tag("recinto")}
        <select
          id={`${idPrefix}-recinto`}
          className="input"
          value={value.recinto}
          disabled={disabled}
          onChange={(e) => set("recinto", e.target.value)}
        >
          <option value="">Selecciona un recinto…</option>
          {listaRecintos.map((r) => (
            <option key={r.codigo} value={r.codigo}>
              {r.nombre}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor={`${idPrefix}-elemento`}>
          Elemento
        </label>
        {tag("elemento")}
        <select
          id={`${idPrefix}-elemento`}
          className="input"
          value={value.elemento}
          disabled={disabled}
          onChange={(e) => set("elemento", e.target.value)}
        >
          <option value="">Selecciona un elemento…</option>
          {catalog.elementos.map((el) => (
            <option key={el.codigo} value={el.codigo}>
              {el.nombre}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor={`${idPrefix}-tipo-falla`}>
          Tipo de observación
        </label>
        {tag("tipoFalla")}
        <select
          id={`${idPrefix}-tipo-falla`}
          className="input"
          value={value.tipoFalla}
          disabled={disabled}
          onChange={(e) => set("tipoFalla", e.target.value)}
        >
          <option value="">Selecciona un tipo de observación…</option>
          {catalog.tiposFalla.map((t) => (
            <option key={t.codigo} value={t.codigo}>
              {t.nombre}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}

/** ¿La partida pertenece a la especialidad? (el botón de guardar lo mira). */
export function partidaDeEspecialidad(
  catalog: TaxonomyCatalog,
  especialidad: string,
  partida: string
): boolean {
  return (
    catalog.especialidades
      .find((e) => e.codigo === especialidad)
      ?.partidas.some((p) => p.codigo === partida) ?? false
  );
}
